import { useEffect, useState } from 'react';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import Paper from '@mui/material/Paper';
import { Avatar, Box, Container, IconButton, Skeleton, Tooltip } from '@mui/material';
import CustomText from 'components/common/CustomText/CustomText';
import { colors } from 'styles/colors';
import CustomButton from 'components/buttons/CustomButton/CustomButton';
import FilterIcon from 'icons/FilterIcon';
import { dateToString, getTranslations } from 'common/utils';
import ImageCarousel from 'components/modals/ImageCarousel/ImageCarousel';
import { IPhotoSize } from 'types/types';
import useGlobalStyles from 'styles/globalStyles';
import { ListProductsDto } from 'types/dto/ListProductsDto';
import CustomCellLabel from 'components/tables/AllUsers/CustomCellLabel';
import CustomCellValue from 'components/tables/AllUsers/CustomCellValue';
import { currencies, tableColumnProps } from 'common/constants';
import ProductsFilters from '../Filters/ProductsFilters';
import { useGetProducts } from 'hooks/useProduct';
import ProductActionButtons from '../ActionButtons/ProductActionButtons';
import ProductsIconOutline from 'icons/ProductsIconOutline';
import { ProductsFilterDto } from 'types/dto/ProductsFilterDto';
import PublishStatus from 'components/statuses/PublishStatus';
import GraphStatus from 'components/statuses/GraphStatus';


export default function AllProductsTable() {
  const globalStyles = useGlobalStyles();
  const [carouselImages, setCarouselImages] = useState<IPhotoSize[]>([]);
  const [carouselOpen, setCarouselOpen] = useState(false);
  const [carouselIndex, setCarouselIndex] = useState<number>(0);
  const [page, setPage] = useState<number>(0);
  const [size, setSize] = useState<number>(10);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [analyserOpen, setAnalyserOpen] = useState<boolean>(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [rowCount, setRowCount] = useState(0);

  const [filters, setFilters] = useState<ProductsFilterDto>({
    barcode: '',
    name: '',
  });

  const requestParams: ListProductsDto = {
    page: page + 1,
    size,
    ...filters,
  };

  useEffect(() => {
    if (selectedId) {
      setAnalyserOpen(true);
    } else {
      setAnalyserOpen(false);
    }
  }, [selectedId]);


  const { data, refetch, isLoading, isRefetching } = useGetProducts(requestParams, {
    refetchOnWindowFocus: false,
    onSuccess: () => { },
    onError: () => console.log('error'),
  });


  const columns: GridColDef[] = [
    {
      field: 'name',
      ...tableColumnProps,
      renderHeader: () => (
        <CustomCellLabel text="Name" />
      ),
      renderCell: (params) => {

        const { ar, en } = getTranslations(params.row.translations);

        return (
          <Box display={'flex'} alignItems={'center'} gap={1}>
            <Box>
              <Avatar
                src={params.row.avatar.small}
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: '5px',
                  bgcolor: colors.avatarBg,
                }}
                onClick={() => {
                  setCarouselImages([params.row.avatar, ...params.row.mediaFiles]);
                  setCarouselIndex(0);
                  setCarouselOpen(true);
                }}
              />
            </Box>
            <Tooltip title={ar.name}>
              <IconButton disableRipple>
                <CustomText
                  text={en.name}
                  fontSize={15}
                  fontWeight={500}
                  color={colors.black}
                />
              </IconButton>
            </Tooltip>
          </Box >
        )
      }
    },
    {
      field: 'price',
      ...tableColumnProps,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => (
        <CustomCellLabel text="Price" />
      ),
      renderCell: (params) => {
        return (
          <Box>
            <CustomCellValue text={`${currencies.shekel}${params.row.price.toFixed(2)}`} />
          </Box >
        )
      }
    },
    {
      field: 'weight',
      ...tableColumnProps,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => (
        <CustomCellLabel text="Weight" />
      ),
      renderCell: (params) => {
        return (
          <Box>
            <CustomCellValue text={`${(params.row.weight || params.row.estimatedWeight)}${params.row.weightUnit}`} />
          </Box >
        )
      }
    },
    {
      field: 'barcode',
      ...tableColumnProps,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => (
        <CustomCellLabel text="Barcode" />
      ),
      renderCell: (params) => {
        return (
          <Box>
            <CustomCellValue text={params.row.barcode} />
          </Box >
        )
      }
    },
    {
      field: 'createdAt',
      ...tableColumnProps,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => (
        <CustomCellLabel text="Date" />
      ),
      renderCell: (params) => {
        return (
          <Box>
            <CustomCellValue text={dateToString(new Date(params.row.createdAt.split('T')[0]))} />
          </Box >
        )
      }
    },
    {
      field: 'tag',
      ...tableColumnProps,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => (
        <CustomCellLabel text="Tag" />
      ),
      renderCell: (params) => {
        return (
          <Tooltip title={params.row.tag?.translations[0].name}>
            <IconButton disableRipple>
              <CustomCellValue text={params.row.tag?.translations[1].name} />
            </IconButton>
          </Tooltip>
        )
      }
    },
    {
      field: 'publishedAt',
      ...tableColumnProps,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => (
        <CustomCellLabel text="Status" />
      ),
      renderCell: (params) => {
        return (
          <Box>
            <PublishStatus publishedAt={params.row.publishedAt} />
          </Box >
        )
      }
    },
    {
      // Beside the publish state, because they are the same question asked of
      // two places: whether the shop shows this product, and whether the graph
      // holds it as it stands.
      field: 'graphSyncedAt',
      ...tableColumnProps,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => (
        <CustomCellLabel text="Graph" />
      ),
      renderCell: (params) => {
        return (
          <Box>
            <GraphStatus
              graphSyncedAt={params.row.graphSyncedAt ?? null}
              graphOutdated={Boolean(params.row.graphOutdated)}
            />
          </Box >
        )
      }
    },
    {
      field: 'actions',
      ...tableColumnProps,
      headerAlign: 'center',
      align: 'center',
      renderHeader: () => (
        <CustomCellLabel text="Actions" />
      ),
      renderCell: (params) => {
        return (
          <Box onClick={(e) => e.stopPropagation()}>
            <ProductActionButtons
              id={params.row.id}
              refetch={refetch}
            />
          </Box>
        )
      }
    },
  ];

  useEffect(() => {
    if (data?.totalCount !== undefined) {
      setRowCount(data.totalCount);
    }
  }, [data?.totalCount]);


  return (
    <Container disableGutters maxWidth={'xl'}>
      <Paper className={globalStyles.tableContainer} sx={{
        overflowX: 'auto',
        minHeight: 11 * 52 + 56, // 10 rows + header
      }}
      >

        {/* Header */}
        <Box
          display={{ sm: 'block', md: 'flex' }}
          justifyContent={'space-between'}
          gap={2}
          sx={{
            px: 2,
            py: 2.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" gap={1.5}>
            <Box
              sx={{
                backgroundColor: colors.topBackground,
                display: 'inline-flex',
                height: 40,
                width: 40,
                justifyContent: 'center',
                alignItems: 'center',
                borderRadius: '8px',
              }}
            >
              <ProductsIconOutline size={24} color={colors.primary} />
            </Box>
            <CustomText text="All Products" fontSize={16} color={colors.primary} fontWeight={600} />
          </Box>
          <Box display={'flex'} gap={2}>
            <CustomButton
              text='Filter'
              variant='outlined'
              height={40}
              icon={<FilterIcon />}
              onClick={() => setFiltersOpen(prev => !prev)}
            />
          </Box>
        </Box>

        <ProductsFilters
          open={filtersOpen}
          initialValues={filters}
          loading={isLoading}
          onApply={(values) => {
            setFilters(values);
            setPage(0);
            refetch();
          }}
          onReset={() => {
            setFilters({ barcode: '', name: '' });
            setPage(0);
            refetch();
          }}
        />

        {/* Data */}
        <DataGrid
          sx={{ minHeight: 11 * 52 + 56 }}
          disableColumnMenu
          disableColumnFilter
          disableColumnSelector
          disableDensitySelector
          disableColumnSorting
          // checkboxSelection
          disableRowSelectionOnClick
          // onRowSelectionModelChange={(selection) => {
          //   console.log('Selected row IDs:', selection);
          // }}
          rows={data?.data || []}
          columns={columns}
          loading={isLoading || isRefetching}
          slotProps={{
            loadingOverlay: {
              children: Array.from({ length: 10 }).map((_, i) => (
                <Skeleton key={i} height={52} />
              )),
            },
          }}
          paginationMode="server"
          rowCount={rowCount}
          paginationModel={{
            page: page,
            pageSize: size,
          }}
          onPaginationModelChange={(model) => {
            setPage(model.page);
            setSize(model.pageSize);
          }}
          pageSizeOptions={[10, 20, 30, 50, 100]}
          onRowClick={(params) => setSelectedId(params.row.id)}
          className={globalStyles.dataGrid}
        />
      </Paper>
      <ImageCarousel
        open={carouselOpen}
        images={carouselImages}
        initialIndex={carouselIndex}
        onClose={() => setCarouselOpen(false)}
      />

      <Box mt={3} />
    </Container>
  );
}
